import { XIcon } from "@phosphor-icons/react";
import { type KeyboardEvent, useRef } from "react";
import { FieldErrorText, Label } from "@/components/ui/field";
import { IconButton } from "@/components/ui/icon-button";
import { JollyTextField } from "@/components/ui/textfield";
import { cn } from "@/lib/utils";

const COMMIT_EMAIL_KEYS = new Set(["Enter", ",", " "]);

const isCommitEmailKey = (key: string) => COMMIT_EMAIL_KEYS.has(key);

const handleCommitKeyDown = (
  event: KeyboardEvent<HTMLInputElement>,
  onCommit: () => void,
) => {
  if (!isCommitEmailKey(event.key)) return;
  event.preventDefault();
  event.stopPropagation();
  onCommit();
};

const chipClassName = (valid: boolean, extraClassName?: string) =>
  cn(
    "flex items-center gap-1 px-2 py-0 rounded-md border text-xs",
    valid
      ? "border-none bg-blue-50 text-blue-700"
      : "border-none bg-red-50 text-red-700",
    extraClassName,
  );

const inlineInputClassName =
  "border-0 bg-transparent p-0 m-0 h-auto min-w-[8ch] focus:ring-0 focus:outline-hidden";

export interface InvalidRecipientEntry {
  id: string;
  value: string;
}

export interface RecipientsListSectionClassNames {
  validChip?: string;
  validChipLabel?: string;
  validChipRemoveButton?: string;
  invalidChip?: string;
  invalidChipRemoveButton?: string;
}

export interface RecipientsListSectionProps {
  validEmails: string[];
  invalidEmails: InvalidRecipientEntry[];
  inputValue: string;
  recipientsError?: string;
  /** Shown below the field when it has no recipients and nothing typed. */
  emptyHint?: string;
  onInputChange: (value: string) => void;
  onCommitInput: () => void;
  onRemoveValidEmail: (email: string) => void;
  onRemoveInvalidEmail: (id: string) => void;
  onInvalidEmailChange: (id: string, value: string) => void;
  onInvalidEmailCommit: (id: string) => void;
  isDisabled?: boolean;
  label?: string;
  description?: string;
  labelVariant?: "default" | "secondary";
  labelSize?: "sm" | "md" | "lg";
  placeholder?: string;
  containerClassName?: string;
  classNames?: RecipientsListSectionClassNames;
}

const RecipientsListSection = ({
  validEmails,
  invalidEmails,
  inputValue,
  recipientsError,
  emptyHint,
  onInputChange,
  onCommitInput,
  onRemoveValidEmail,
  onRemoveInvalidEmail,
  onInvalidEmailChange,
  onInvalidEmailCommit,
  isDisabled = false,
  label = "Recipients",
  description,
  labelVariant = "default",
  labelSize = "md",
  placeholder = "Add recipients...",
  containerClassName,
  classNames,
}: RecipientsListSectionProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasRecipients = validEmails.length > 0 || invalidEmails.length > 0;

  const focusAddInput = (target: EventTarget | null) => {
    if (isDisabled) return;
    if (!(target instanceof HTMLElement)) return;
    if (target.closest("[data-recipient-chip]")) return;
    inputRef.current?.focus();
  };

  return (
    <div className="flex flex-col gap-1">
      <Label
        aria-label="recipients-label"
        variant={labelVariant}
        size={labelSize}
        className={cn(
          labelVariant === "default" && "text-sm font-medium text-gray-700",
        )}
      >
        {label}
      </Label>
      {description ? (
        <p className="text-sm leading-5 text-gray-600">{description}</p>
      ) : null}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: focuses add-recipient input when container is clicked; matches news alerts recipients field */}
      <div
        className={cn(
          "border border-slate-200 rounded-md px-3 py-1 flex flex-wrap items-center gap-2 focus-within:border-blue-500 cursor-text min-h-[40px]",
          containerClassName,
          recipientsError && "border-red-600 focus-within:border-red-600",
          isDisabled && "cursor-not-allowed opacity-50",
        )}
        onClick={(event) => focusAddInput(event.target)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && event.target === event.currentTarget) {
            event.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        {validEmails.map((email) => (
          <div
            key={email}
            data-recipient-chip
            className={chipClassName(true, classNames?.validChip)}
          >
            <span className={classNames?.validChipLabel}>{email}</span>
            <IconButton
              variant="icon-only"
              icon={<XIcon size={14} />}
              onPressChange={() => onRemoveValidEmail(email)}
              aria-label={`Remove ${email}`}
              className={cn(
                "p-0 text-gray-700",
                classNames?.validChipRemoveButton,
              )}
              isDisabled={isDisabled}
              excludeFromTabOrder
            />
          </div>
        ))}

        {invalidEmails.map((entry) => (
          <div
            key={entry.id}
            data-recipient-chip
            className={chipClassName(
              false,
              cn("[&_[slot=errorMessage]]:hidden", classNames?.invalidChip),
            )}
          >
            <JollyTextField
              size="sm"
              label={undefined}
              aria-label={`Fix invalid email ${entry.value || "empty"}`}
              isInvalid
              value={entry.value}
              onChange={(value) => onInvalidEmailChange(entry.id, value)}
              onBlur={() => onInvalidEmailCommit(entry.id)}
              onKeyDown={(event) =>
                handleCommitKeyDown(event, () => onInvalidEmailCommit(entry.id))
              }
              className={cn(
                inlineInputClassName,
                "field-sizing-content min-w-[1ch] text-xs text-red-700",
              )}
              clearable={false}
              isDisabled={isDisabled}
            />
            <IconButton
              variant="icon-only"
              icon={<XIcon size={14} />}
              onPressChange={() => onRemoveInvalidEmail(entry.id)}
              aria-label={`Remove invalid email ${entry.value || "empty"}`}
              className={cn(
                "p-0 text-gray-700",
                classNames?.invalidChipRemoveButton,
              )}
              isDisabled={isDisabled}
              excludeFromTabOrder
            />
          </div>
        ))}

        <div className="flex-1 min-w-[120px]">
          <JollyTextField
            size="sm"
            ref={inputRef}
            label={undefined}
            aria-label="add-recipient-input"
            description={undefined}
            placeholder={hasRecipients ? "" : placeholder}
            value={inputValue}
            onChange={onInputChange}
            onBlur={() => {
              if (inputValue.trim()) onCommitInput();
            }}
            onKeyDown={(event) => handleCommitKeyDown(event, onCommitInput)}
            className={cn(
              inlineInputClassName,
              "p-1 text-xs focus:ring-0 focus:outline-hidden",
            )}
            clearable={false}
            isDisabled={isDisabled}
          />
        </div>
      </div>
      {recipientsError ? (
        <FieldErrorText>{recipientsError}</FieldErrorText>
      ) : emptyHint && !hasRecipients && !inputValue.trim() ? (
        <p className="text-sm leading-5 text-gray-600">{emptyHint}</p>
      ) : null}
    </div>
  );
};

export default RecipientsListSection;
